import { DirectionProvider } from '@radix-ui/react-direction';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { useState, type ReactElement, type ReactNode } from 'react';
import { useLayerContainer } from './layer';

/** Wrap the app once: Radix learns the reading direction (menus, tabs, keyboard) and tooltips share one delay. */
export function UiProvider({ dir, children }: { dir: 'ltr' | 'rtl'; children: ReactNode }) {
  return (
    <DirectionProvider dir={dir}>
      <TooltipPrimitive.Provider delayDuration={350} skipDelayDuration={150}>
        {children}
      </TooltipPrimitive.Provider>
    </DirectionProvider>
  );
}

export { useDirection } from '@radix-ui/react-direction';

export interface TooltipProps {
  label: ReactNode;
  /** One element that can hold a ref (a button, a link). */
  children: ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Never opens — for when the label is already on screen (the rail with its names). The child stays mounted, focus and all. */
  disabled?: boolean;
}

export function Tooltip({ label, children, side = 'bottom', align = 'center', disabled = false }: TooltipProps) {
  const container = useLayerContainer();
  // always controlled: Radix warns when a tooltip switches between controlled and uncontrolled
  const [open, setOpen] = useState(false);
  return (
    <TooltipPrimitive.Root open={open && !disabled} onOpenChange={(next) => setOpen(next && !disabled)}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal container={container}>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          className="vlm-fade z-tooltip max-w-[280px] rounded-sm bg-surface-inverse px-2 py-1 text-caption text-ink-inverse shadow-2"
        >
          {label}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
